import { Component, computed, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { DashChart, DashFormat } from '@shared/services/entities/dashboard-summary.service';

/** Paleta validada (CVD + contraste) — el 3er tono siempre va con etiqueta. */
const SERIES = ['#1010EB', '#00A3A1', '#E07B00'];
/** Colores de estado: solo para gráficas cuyas categorías SON un estado. */
const STATUS: Record<string, string[]> = {
  expiry: ['#14853D', '#B26B00', '#C62828'],   // > 60 · 46-60 · ≤ 45 días
  billing: ['#14853D', '#5A5F7A', '#C62828'],  // Facturada · Pendiente · Con novedad
};

const W = 560, H = 230, PL = 44, PR = 12, PT = 18, PB = 28;

export function fmtValue(v: number, f: DashFormat | undefined, compact = false): string {
  if (v == null || isNaN(v)) return '—';
  if (f === 'money') {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency', currency: 'MXN', maximumFractionDigits: compact ? 1 : 0,
      notation: compact ? 'compact' : 'standard',
    }).format(v);
  }
  if (f === 'days') return `${v.toLocaleString('es-MX', { maximumFractionDigits: 1 })} días`;
  return compact
    ? new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 }).format(v)
    : v.toLocaleString('es-MX');
}

/**
 * Gráfica ligera del tablero ejecutivo (SVG/HTML, sin librerías).
 * Tipos: bar · line · hbar · donut. Un donut con más de 3 categorías se
 * dibuja como barras horizontales (más fácil de leer y sin colores extra).
 */
@Component({
  selector: 'app-exec-chart',
  standalone: true,
  template: `
  @let c = chart();
  <div class="xc__head">
    <div>
      <h3>{{ c.title }}</h3>
      @if (c.subtitle) { <span>{{ c.subtitle }}</span> }
    </div>
    @if (c.route) {
      <button type="button" class="xc__go" (click)="open.emit(c.route)" aria-label="Ver detalle">Ver detalle →</button>
    }
  </div>

  @if (c.series.length > 1) {
    <div class="xc__legend">
      @for (s of c.series; track s.name; let i = $index) {
        <span><i [style.background]="color(i)"></i>{{ s.name }} · <b>{{ fmt(sum(s.values)) }}</b></span>
      }
    </div>
  }

  @switch (kind()) {
    @case ('bar') {
      <div class="xc__plot">
        <svg [attr.viewBox]="'0 0 ' + W + ' ' + H" role="img" [attr.aria-label]="c.title">
          @for (t of ticks(); track t.v) {
            <line class="grid" [attr.x1]="PL" [attr.x2]="W - PR" [attr.y1]="t.y" [attr.y2]="t.y" />
            <text class="ax" [attr.x]="PL - 6" [attr.y]="t.y + 4" text-anchor="end">{{ fmtC(t.v) }}</text>
          }
          @for (l of c.labels; track $index; let i = $index) {
            @let b = barGeom(i);
            @for (s of c.series; track s.name; let j = $index) {
              @let h = yScale(s.values[i] ?? 0);
              <path [attr.d]="roundTop(b.x + j * b.w, H - PB - h, b.w - 2, h)" [attr.fill]="color(j)" />
            }
            @if (showLabel(i)) {
              <text class="val" [attr.x]="b.cx" [attr.y]="H - PB - yScale(stackMax(i)) - 5" text-anchor="middle">{{ fmtC(stackMax(i)) }}</text>
            }
            <text class="ax" [attr.x]="b.cx" [attr.y]="H - 8" text-anchor="middle">{{ l }}</text>
            <rect class="hit" [attr.x]="b.slot0" [attr.y]="PT" [attr.width]="b.slot" [attr.height]="H - PT - PB"
                  (mouseenter)="hover.set(i)" (mouseleave)="hover.set(null)" />
          }
        </svg>
        @if (hover() !== null) { <ng-container *ngTemplateOutlet="tip" /> }
      </div>
    }
    @case ('line') {
      <div class="xc__plot">
        <svg [attr.viewBox]="'0 0 ' + W + ' ' + H" role="img" [attr.aria-label]="c.title">
          @for (t of ticks(); track t.v) {
            <line class="grid" [attr.x1]="PL" [attr.x2]="W - PR" [attr.y1]="t.y" [attr.y2]="t.y" />
            <text class="ax" [attr.x]="PL - 6" [attr.y]="t.y + 4" text-anchor="end">{{ fmtC(t.v) }}</text>
          }
          @if (hover() !== null) {
            <line class="cross" [attr.x1]="px(hover()!)" [attr.x2]="px(hover()!)" [attr.y1]="PT" [attr.y2]="H - PB" />
          }
          @for (s of c.series; track s.name; let j = $index) {
            <polyline [attr.points]="linePoints(s.values)" fill="none" [attr.stroke]="color(j)" stroke-width="2"
                      stroke-linejoin="round" stroke-linecap="round" />
            @let last = s.values.length - 1;
            <circle [attr.cx]="px(last)" [attr.cy]="H - PB - yScale(s.values[last] ?? 0)" r="4" [attr.fill]="color(j)" stroke="#fff" stroke-width="2" />
            @if (c.series.length <= 2) {
              <text class="val" [attr.x]="px(last) - 6" [attr.y]="H - PB - yScale(s.values[last] ?? 0) - 8" text-anchor="end">{{ fmtC(s.values[last] ?? 0) }}</text>
            }
          }
          @for (l of c.labels; track $index; let i = $index) {
            @if (c.labels.length <= 8 || i % 2 === (c.labels.length - 1) % 2) {
              <text class="ax" [attr.x]="px(i)" [attr.y]="H - 8" text-anchor="middle">{{ l }}</text>
            }
            <rect class="hit" [attr.x]="px(i) - step() / 2" [attr.y]="PT" [attr.width]="step()" [attr.height]="H - PT - PB"
                  (mouseenter)="hover.set(i)" (mouseleave)="hover.set(null)" />
          }
        </svg>
        @if (hover() !== null) { <ng-container *ngTemplateOutlet="tip" /> }
      </div>
    }
    @case ('hbar') {
      <div class="xc__hbars">
        @for (r of rows(); track r.label) {
          <div class="hb" [attr.title]="r.label + ': ' + fmt(r.value)">
            <span class="hb__label">{{ r.label }}</span>
            <span class="hb__track"><span class="hb__fill" [style.width.%]="r.pct" [style.background]="r.color"></span></span>
            <b class="hb__val">{{ fmt(r.value) }}</b>
          </div>
        }
      </div>
    }
    @case ('donut') {
      <div class="xc__donut">
        <svg viewBox="0 0 120 120" role="img" [attr.aria-label]="c.title">
          @for (a of arcs(); track a.label) {
            <path [attr.d]="a.d" [attr.fill]="a.color" stroke="#fff" stroke-width="2"
                  (mouseenter)="hover.set(a.i)" (mouseleave)="hover.set(null)" />
          }
          <text x="60" y="58" text-anchor="middle" class="donut__num">{{ fmtC(total()) }}</text>
          <text x="60" y="74" text-anchor="middle" class="donut__lbl">Total</text>
        </svg>
        <ul>
          @for (a of arcs(); track a.label) {
            <li [class.on]="hover() === a.i">
              <i [style.background]="a.color"></i>
              <span>{{ a.label }}</span>
              <b>{{ fmt(a.value) }}</b>
              <small>{{ a.pct }}%</small>
            </li>
          }
        </ul>
      </div>
    }
  }

  @if (empty()) { <p class="xc__empty">Sin datos para mostrar todavía.</p> }

  <ng-template #tip>
    @let i = hover()!;
    <div class="xc__tip" [style.left.%]="tipLeft(i)">
      <b>{{ c.labels[i] }}</b>
      @for (s of c.series; track s.name; let j = $index) {
        <span><i [style.background]="color(j)"></i>{{ s.name }}: <b>{{ fmt(s.values[i] ?? 0) }}</b></span>
      }
    </div>
  </ng-template>
  `,
  styles: [`
    :host { display: flex; flex-direction: column; gap: 10px; padding: 16px 16px 12px; background: #fff;
            border: 1px solid #e3e5ef; border-radius: 14px; min-width: 0; color: #170f5f; }
    .xc__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
    h3 { margin: 0; font-size: 15px; font-weight: 700; }
    .xc__head span { font-size: 12px; color: #5a5f7a; }
    .xc__go { border: 0; background: none; color: #1010eb; font-weight: 600; font-size: 12.5px; cursor: pointer; padding: 2px 4px; white-space: nowrap; }
    .xc__go:hover { text-decoration: underline; }
    .xc__legend { display: flex; flex-wrap: wrap; gap: 14px; font-size: 12.5px; color: #4a4f6a; }
    .xc__legend i, .xc__tip i, li i { display: inline-block; width: 10px; height: 10px; border-radius: 3px; margin-right: 6px; vertical-align: -1px; }
    .xc__plot { position: relative; }
    svg { width: 100%; height: auto; display: block; overflow: visible; }
    .grid { stroke: #eceef5; stroke-width: 1; }
    .cross { stroke: #9aa0b8; stroke-width: 1; stroke-dasharray: 3 3; }
    .ax { font-size: 11px; fill: #5a5f7a; }
    .val { font-size: 11px; font-weight: 700; fill: #170f5f; }
    .hit { fill: transparent; cursor: crosshair; }
    .xc__tip { position: absolute; top: 4px; transform: translateX(-50%); pointer-events: none; background: #170f5f; color: #fff;
               border-radius: 8px; padding: 8px 10px; font-size: 12px; display: flex; flex-direction: column; gap: 3px;
               box-shadow: 0 6px 18px rgba(23,15,95,.18); white-space: nowrap; z-index: 2; }
    .xc__hbars { display: flex; flex-direction: column; gap: 8px; }
    .hb { display: grid; grid-template-columns: minmax(90px, 38%) 1fr auto; align-items: center; gap: 10px; font-size: 12.5px; }
    .hb__label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #4a4f6a; }
    .hb__track { height: 12px; background: #f1f2f8; border-radius: 4px; overflow: hidden; }
    .hb__fill { display: block; height: 100%; border-radius: 0 4px 4px 0; min-width: 2px; }
    .hb__val { font-variant-numeric: tabular-nums; min-width: 40px; text-align: right; }
    .xc__donut { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; }
    .xc__donut svg { width: 150px; flex: 0 0 150px; }
    .donut__num { font-size: 18px; font-weight: 800; fill: #170f5f; }
    .donut__lbl { font-size: 10px; fill: #5a5f7a; }
    ul { list-style: none; margin: 0; padding: 0; flex: 1; min-width: 160px; display: flex; flex-direction: column; gap: 6px; }
    li { display: grid; grid-template-columns: auto 1fr auto auto; align-items: center; gap: 6px; font-size: 12.5px; padding: 2px 4px; border-radius: 6px; }
    li.on { background: #f1f2f8; }
    li small { color: #5a5f7a; min-width: 34px; text-align: right; }
    li b { font-variant-numeric: tabular-nums; }
    .xc__empty { margin: 0; font-size: 12.5px; color: #5a5f7a; }
  `],
  imports: [NgTemplateOutlet],
})
export class ExecChartComponent {
  readonly chart = input.required<DashChart>();
  readonly open = output<string>();

  readonly W = W; readonly H = H; readonly PL = PL; readonly PR = PR; readonly PT = PT; readonly PB = PB;
  readonly hover = signal<number | null>(null);

  /** Donut con más de 3 categorías → barras horizontales. */
  readonly kind = computed(() => {
    const c = this.chart();
    if (c.type === 'donut' && !STATUS[c.key] && c.labels.length > 3) return 'hbar';
    return c.type;
  });
  readonly empty = computed(() => this.chart().series.every(s => s.values.every(v => !v)));
  readonly total = computed(() => this.sum(this.chart().series[0]?.values ?? []));

  readonly maxY = computed(() => {
    const c = this.chart();
    const m = Math.max(0, ...c.labels.map((_, i) => Math.max(...c.series.map(s => s.values[i] ?? 0))));
    return niceMax(m);
  });
  readonly ticks = computed(() => {
    const m = this.maxY();
    return [0, 0.5, 1].map(f => ({ v: m * f, y: H - PB - (H - PT - PB) * f }));
  });
  readonly step = computed(() => {
    const n = Math.max(1, this.chart().labels.length);
    return (W - PL - PR) / n;
  });

  readonly rows = computed(() => {
    const c = this.chart();
    const vals = c.series[0]?.values ?? [];
    const max = Math.max(1, ...vals);
    const st = STATUS[c.key];
    return c.labels.map((label, i) => ({
      label, value: vals[i] ?? 0, pct: Math.round(100 * (vals[i] ?? 0) / max),
      color: st ? st[i] ?? SERIES[0] : SERIES[0],
    }));
  });

  readonly arcs = computed(() => {
    const c = this.chart();
    const vals = c.series[0]?.values ?? [];
    const tot = this.sum(vals) || 1;
    const pal = STATUS[c.key] ?? SERIES;
    let a0 = -Math.PI / 2;
    return c.labels.map((label, i) => {
      const v = vals[i] ?? 0;
      const a1 = a0 + 2 * Math.PI * (v / tot) * 0.99999;
      const d = v ? donutArc(60, 60, 56, 38, a0, a1) : '';
      a0 = a1;
      return { i, label, value: v, pct: Math.round(100 * v / tot), color: pal[i] ?? SERIES[0], d };
    });
  });

  color(i: number): string { return SERIES[i] ?? SERIES[SERIES.length - 1]; }
  sum(v: number[]): number { return v.reduce((a, b) => a + (b || 0), 0); }
  fmt(v: number): string { return fmtValue(v, this.chart().format); }
  fmtC(v: number): string { return fmtValue(v, this.chart().format, true); }

  yScale(v: number): number { return (H - PT - PB) * (v || 0) / this.maxY(); }
  px(i: number): number { return PL + this.step() * (i + 0.5); }
  linePoints(v: number[]): string {
    return v.map((y, i) => `${this.px(i).toFixed(1)},${(H - PB - this.yScale(y)).toFixed(1)}`).join(' ');
  }
  barGeom(i: number) {
    const slot = this.step(), n = this.chart().series.length;
    const group = Math.min(slot * 0.62, 26 * n);
    const w = group / n;
    const slot0 = PL + slot * i;
    return { slot, slot0, w, x: slot0 + (slot - group) / 2, cx: slot0 + slot / 2 };
  }
  stackMax(i: number): number { return Math.max(...this.chart().series.map(s => s.values[i] ?? 0)); }
  /** Etiqueta directa solo en el último valor y en el máximo (no en todas las barras). */
  showLabel(i: number): boolean {
    const c = this.chart(), n = c.labels.length;
    if (n <= 8) return this.stackMax(i) > 0;
    const vals = c.labels.map((_, k) => this.stackMax(k));
    return vals[i] > 0 && (i === n - 1 || vals[i] === Math.max(...vals));
  }
  roundTop(x: number, y: number, w: number, h: number): string {
    if (h <= 0) return '';
    const r = Math.min(4, w / 2, h);
    return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
  }
  tipLeft(i: number): number {
    const p = 100 * this.px(i) / W;
    return Math.min(82, Math.max(18, p));
  }
}

function niceMax(m: number): number {
  if (m <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(m)));
  for (const k of [1, 2, 2.5, 5, 10]) if (k * p >= m) return k * p;
  return 10 * p;
}

function donutArc(cx: number, cy: number, R: number, r: number, a0: number, a1: number): string {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const p = (rad: number, a: number) => `${(cx + rad * Math.cos(a)).toFixed(2)},${(cy + rad * Math.sin(a)).toFixed(2)}`;
  return `M${p(R, a0)}A${R},${R} 0 ${large} 1 ${p(R, a1)}L${p(r, a1)}A${r},${r} 0 ${large} 0 ${p(r, a0)}Z`;
}
